<script lang="ts">
  import { onMount } from "svelte"
  import { Pulse } from "svelte-loading-spinners"
  import Sortable from "sortablejs"
  import Department from "./Departments/Department.svelte"
  import DepartmentForm from "./Departments/DepartmentForm.svelte"
  import { xhr, toast, wait, sortElementsForSortable } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { appStore } from "$app/store.svelte"

  let departments: DepartmentType[] = $state([])

  const newDeptData = { name: "" } as DepartmentType

  function initializeDepartment(dept: DepartmentType): DepartmentType {
    return {
      ...dept,
      _selected: false
    }
  }

  onMount(async () => {
    departments = departments.map((d) => initializeDepartment(d))
    initialize()
  })

  let isLoading = $state(true)
  async function initialize(): Promise<void> {
    isLoading = true
    try {
      const resp = await xhr.get(`/plans/${appStore.plan.id}/departments`)

      let data = resp.data

      departments = data.departments
    } catch (e) {
      toast("There was an error loading the plan departments", "error")
    } finally {
      isLoading = false
      setTimeout(() => {
        setSortable()
      }, 500)
    }
  }

  // Create department
  let saving = $state(false)
  let newDept = $state(false)
  let errors: Record<string, string[]> = $state({})

  async function createDepartment(data: { name: string }) {
    errors = {}
    saving = true

    try {
      const resp = await xhr.post(`/plans/${appStore.plan.id}/departments`, {
        name: data.name
      })

      departments = [...departments, resp.data]
      toast("Department created", "success")
      newDept = false
    } catch (e) {
      errors = e.response?.data?.errors || {}
      Honeybadger.notify(e)
      toast("There was an error creating the department", "error")
    } finally {
      saving = false
    }
  }

  // Delete deparment
  async function deleteDepartment(department: DepartmentType): Promise<void> {
    department._selected = true
    departments = departments

    try {
      xhr.delete(`/plans/${appStore.plan.id}/departments/${department.id}`)
      departments = departments
      toast(
        `Department was archived.<br/> you can restore it going to <a class="text-blue-700" href="/plans/${appStore.plan.id}/archive">archive</a>`,
        "success"
      )
    } catch (e) {
      Honeybadger.notify(e)
      toast("There was an error archiving the department", "error")
    } finally {
      department._selected = false
      const idx = departments.findIndex((d) => d.id === department.id)
      departments.splice(idx, 1)
      departments = departments
    }
  }

  let sortableSection: HTMLElement = $state()

  function setSortable(): void {
    let timeoutHandler: any = null
    const delay = 20
    let elem: HTMLElement

    let scrollFn: Function

    if (!sortableSection) return

    const sort = Sortable.create(sortableSection, {
      animation: 200,
      group: "departments",
      scrollSensitivity: 150,
      delay: 400,
      delayOnTouchOnly: true,
      direction: "vertical",
      ghostClass: "opacity-50",
      dragClass: "border-green-400",
      emptyInsertThreshold: 20,
      onEnd: async (e: EventSortType) => {
        if (e.newIndex == e.oldIndex) return
        sort.options.sort = false // Disable sorting while saving
        try {
          await saveSortOrder(
            sortElementsForSortable(departments, e.newIndex, e.oldIndex)
          )
        } catch (e) {
          console.log(e)
        } finally {
          sort.options.sort = true
        }
      },
      onStart: (evt: EventSortType) => {
        const contHeight = evt.srcElement.clientHeight
        const itemHeight = evt.item.clientHeight

        scrollFn = evt.item.ondrag = (mevt: MouseEvent) => {
          if (timeoutHandler == null) {
            timeoutHandler = setTimeout(function () {
              const offsetClientY = mevt.clientY + 20

              if (offsetClientY > contHeight) {
                elem.scrollBy(0, (offsetClientY / 40) * 5)
              } else if (mevt.clientY - itemHeight < 0) {
                elem.scrollBy(
                  0,
                  (Math.abs(mevt.clientY - itemHeight) / 30) * -10
                )
              }
              timeoutHandler = null
            }, delay)
          }
        }
      }
    })

    elem = sort.el
  }

  async function saveSortOrder(
    newDepartments: DepartmentType[]
  ): Promise<void> {
    try {
      await xhr.post(`/plans/${appStore.plan.id}/departments/sort.json`, {
        department_ids: newDepartments.map((st) => st.id)
      })
    } catch (e) {
      Honeybadger.notify(e)
      toast("There was an error updating the sort order", "error")
      let tempDepartments = [...departments]
      departments = tempDepartments
    }
  }
</script>

<div class="flex flex-col max-w-4xl mx-auto py-6">
  <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
    <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
      {#if isLoading}
        <div class="py-12 flex justify-center w-full">
          <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
        </div>
      {:else}
        <div class="overflow-hidden">
          <ul
            class="min-w-full my-2 hover:cursor-move"
            bind:this={sortableSection}
            role="list"
          >
            {#each departments as department, idx (department.id)}
              <li class="mb-2 border border-gray-200">
                <Department
                  {department}
                  ondelete={(department) => deleteDepartment(department)}
                />
              </li>
            {/each}

            {#if newDept}
              <li>
                <div
                  class="border border-gray-100 border-rounded bg-gray-50 shadow py-4 px-2"
                >
                  <p class="text-center text-lg font-bold">New Department</p>
                  <DepartmentForm
                    department={newDeptData}
                    buttonMsg="Create"
                    {saving}
                    oncancel={() => (newDept = false)}
                    ondata={(e) => createDepartment(e)}
                    error={errors.name}
                  />
                </div>
              </li>
            {/if}
          </ul>
        </div>
      {/if}
    </div>
  </div>
</div>

{#if !newDept}
  <div class="w-full text-center mt-6">
    <button
      class="btn btn-outline text-base"
      onclick={() => {
        newDept = true
      }}>Add New Department</button
    >
  </div>
{/if}
